import { Avatar, Box, Link, Stack, styled, Typography } from '@ori-ui/mui';
import type { ReferralProgram } from '@ori/graphql-schema';
import { DynamicFormattedMessage } from '@ori/i18n';
import useTestId from '@ori/testid-generator';
import type { DeepReadonly } from '@ori/ts-utils';
import type { DetailedHTMLProps, FC, HTMLAttributes } from 'react';

import { useTranslations } from '../../hooks/useTranslations';
import { MenuContentTestIds, TranslationKey } from '../../models';
import type { MenuItemPersonalLinksProps } from '../MenuItem/MenuItemPersonalLinks';
import { MenuItemPersonalLinks } from '../MenuItem/MenuItemPersonalLinks';

/**
 * Avatar size. Value is in pixels
 */
const AVATAR_SIZE = 170;

/**
 * Maximal content width. Value is in pixels
 */
const MAX_CONTENT_WIDTH = 658;

const Root = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.common.white,
  padding: theme.spacing(3),
  maxWidth: MAX_CONTENT_WIDTH,
}));

const ItemContentWrap = styled(Stack)(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(2.5),
}));

const MenuItemPersonalLinksWrap = styled(Box)(({ theme }) => ({
  svg: { width: 22, height: 24, ml: theme.spacing(0.8), mr: theme.spacing(0.6) },
}));

const LinkWrap = styled(Typography)(({ theme }) => ({
  display: 'block',
  flexDirection: 'row',
  alignItems: 'center',
  marginTop: theme.spacing(1.5),
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  margin: theme.spacing(4),
}));

export type MenuContentBaseProps = DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export type MenuContentProps = MenuContentBaseProps &
  MenuItemPersonalLinksProps & {
    photoUrl: string | null | undefined;
    referralProgram: DeepReadonly<ReferralProgram> | null | undefined;
    userEmail: string | null | undefined;
    userPhone: string | null | undefined;
  };

export const MenuContent: FC<MenuContentProps> = ({
  photoUrl,
  referralProgram,
  userEmail,
  userPhone,
  liveStreamingState,
  personalProductListConfiguration,
  videoShoppingConfiguration,
  ...restProps
}) => {
  const { getTestIdProps } = useTestId();
  const { formatMessage } = useTranslations();

  const { benefitsLink, greetingMessage, message } = referralProgram ?? {};

  return (
    <Root
      sx={{ display: 'flex' }}
      {...getTestIdProps(MenuContentTestIds.ROOT)}
      {...restProps}
    >
      <Box sx={{ justifyContent: 'flex-start' }}>
        <ItemContentWrap
          direction="column"
          spacing={2}
        >
          <Typography
            variant="h6"
            color="textPrimary"
            {...getTestIdProps(MenuContentTestIds.HEADING)}
          >
            {greetingMessage}
          </Typography>
          {message ? (
            <Typography
              variant="body2"
              color="textPrimary"
              {...getTestIdProps(MenuContentTestIds.TEXT)}
            >
              <DynamicFormattedMessage
                translatedMessage={message}
                values={{
                  // eslint-disable-next-line react/no-unstable-nested-components -- ok
                  emphasis: (chunks) => <strong {...getTestIdProps(MenuContentTestIds.USER_NAME)}>{chunks}</strong>,
                }}
              />
            </Typography>
          ) : null}
          {benefitsLink?.relativeUrl && benefitsLink.title ? (
            <LinkWrap>
              <Link
                href={benefitsLink.relativeUrl}
                variant="button"
                {...getTestIdProps(MenuContentTestIds.BENEFITS_LINK)}
              >
                {benefitsLink.title}
              </Link>
            </LinkWrap>
          ) : null}
          {userEmail ? (
            <LinkWrap>
              <Link
                href={`mailto:${userEmail}`}
                {...getTestIdProps(MenuContentTestIds.USER_EMAIL)}
              >
                {userEmail}
              </Link>
            </LinkWrap>
          ) : null}
          {userPhone ? (
            <LinkWrap>
              <Link
                href={`tel:${userPhone}`}
                {...getTestIdProps(MenuContentTestIds.USER_PHONE)}
              >
                {userPhone}
              </Link>
            </LinkWrap>
          ) : null}
          <MenuItemPersonalLinksWrap>
            <MenuItemPersonalLinks
              personalProductListConfiguration={personalProductListConfiguration}
              videoShoppingConfiguration={videoShoppingConfiguration}
              liveStreamingState={liveStreamingState}
              photoUrl={photoUrl}
            />
          </MenuItemPersonalLinksWrap>
        </ItemContentWrap>
      </Box>
      {photoUrl ? (
        <Box sx={{ justifyContent: 'flex-end' }}>
          <StyledAvatar
            src={photoUrl}
            alt={formatMessage(TranslationKey.SponsorPhoto)}
            {...getTestIdProps(MenuContentTestIds.AVATAR)}
          />
        </Box>
      ) : null}
    </Root>
  );
};
